// Authentication state + flows.
//  - Email/password: fully implemented; works as soon as the API has CORS enabled.
//  - Google/Microsoft: uses the Office Dialog API to host the provider sign-in (popups are
//    blocked inside the task-pane iframe). Requires Firebase web config + the auth dialog
//    page (public/auth-dialog.html). Gated behind isOAuthConfigured().
import { reactive } from 'vue'
import * as api from './api'
import { isOAuthConfigured } from './config'
import type { Provider, UserSession } from '../types/auth'

export const auth = reactive<{
  user: UserSession | null
  status: 'idle' | 'loading' | 'error'
  error: string | null
  ready: boolean // session-restore finished
}>({
  user: null,
  status: 'idle',
  error: null,
  ready: false,
})

export function isAuthenticated(): boolean {
  return auth.user !== null
}

/** On startup: if we have a token, validate it and restore the session. */
export async function restoreSession(): Promise<void> {
  if (!api.getAccessToken()) {
    auth.ready = true
    return
  }
  try {
    const res = await api.validateSession()
    auth.user = res.user
  } catch {
    api.clearTokens()
    auth.user = null
  } finally {
    auth.ready = true
  }
}

export async function signInWithEmail(email: string, password: string): Promise<void> {
  auth.status = 'loading'
  auth.error = null
  try {
    const res = await api.authenticateEmail(email.trim(), password)
    api.setTokens(res.accessToken, res.refreshToken)
    auth.user = res.user
    auth.status = 'idle'
  } catch (err) {
    auth.status = 'error'
    auth.error = errorMessage(err)
    throw err
  }
}

export async function signInWithProvider(provider: Exclude<Provider, 'Email'>): Promise<void> {
  if (!isOAuthConfigured()) {
    throw new Error(
      'Provider sign-in is not configured yet. Add the Firebase web config (see GO-LIVE.md, Phase 2).',
    )
  }
  auth.status = 'loading'
  auth.error = null
  try {
    // Login only: the add-in does NOT create accounts. New users sign up on the web first; here we
    // just exchange the provider token for a session. If no account exists, the API returns
    // "No account found" and we surface it (the sign-in screen links to web signup).
    const providerToken = await openAuthDialog(provider)
    const res = await api.authenticateProvider(providerToken, provider)
    api.setTokens(res.accessToken, res.refreshToken)
    auth.user = res.user
    auth.status = 'idle'
  } catch (err) {
    auth.status = 'error'
    auth.error = errorMessage(err)
    throw err
  }
}

export async function signOut(): Promise<void> {
  try {
    await api.signOut()
  } catch {
    /* even if the server call fails, drop local state */
  }
  api.clearTokens()
  auth.user = null
  auth.status = 'idle'
}

/**
 * Open the provider sign-in in an Office dialog and resolve with the provider token the
 * dialog posts back via Office.context.ui.messageParent(...). The dialog page
 * (auth-dialog.html) performs the Firebase sign-in.
 */
function openAuthDialog(provider: Exclude<Provider, 'Email'>): Promise<string> {
  return new Promise((resolve, reject) => {
    if (typeof Office === 'undefined' || !Office.context?.ui?.displayDialogAsync) {
      reject(new Error('Provider sign-in requires running inside Office.'))
      return
    }
    // Use the extensionless path: Cloudflare Pages serves clean URLs and 308-redirects
    // /auth-dialog.html -> /auth-dialog, which the Office dialog loader won't follow.
    const url = `${window.location.origin}/auth-dialog?provider=${provider}`
    Office.context.ui.displayDialogAsync(url, { height: 60, width: 40 }, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error('Could not open the sign-in dialog.'))
        return
      }
      const dialog = result.value
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        const message = (arg as { message?: string }).message ?? ''
        dialog.close()
        try {
          const parsed = JSON.parse(message) as { providerToken?: string; error?: string }
          if (parsed.providerToken) resolve(parsed.providerToken)
          else reject(new Error(parsed.error || 'Sign-in failed.'))
        } catch {
          reject(new Error('Malformed response from sign-in dialog.'))
        }
      })
      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
        reject(new Error('Sign-in dialog was closed.'))
      })
    })
  })
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}
